import { createSelector } from 'reselect';
import { RootState } from '../../../../types';
import { EntityTypes } from '../../../../constants/entities';
import { ActionStatus, ActionType, IActionBasic } from 'types/domain/action';
import { IEntityReducer } from '../../../reducers/entities';
import { Handles } from 'constants/handles';
import { parseAction, parseActionDAO } from './action';
import { selectFeed } from '../feed';
import { ActionHistory, ActionHistoryItem, selectActionHistory, selectSimulatedActionHistory } from './actionHistory';
import { IModuleAssign, IModuleOut, ModuleType } from 'types/domain/module';
import { IRegisterReducer } from 'core/slices/register';
import { IQueueItem } from 'core/slices/queue';
import { getFirstAction } from './actionFirst';
import { getEntities } from 'core/selectors/utils/getEntities';
import { Mode } from 'core/slices/session';

export interface IActionFeed {
  feed: IActionFeedItem[];
  index: number;
  next: boolean;
}

export interface IActionFeedItem {
  actionId?: number;
  actionType?: ActionType;
  sceneId?: number;
  sessionAttemptId?: number;
  courseProgressId?: number;
  actionRegisterId: string;
  verificationRequestId?: number;
  courseNodeId?: number;
  moduleId?: number;
  feedback?: boolean;
}

const entity = (state: RootState) => state.entity.present;
const feed = selectFeed;
const actionHistory = selectActionHistory;
const simulatedActionHistory = selectSimulatedActionHistory;
const mode = (state: RootState) => state.session.mode;
const register = (state: RootState) => state.register;

const mergeHistory = (simulatedActionHistory: ActionHistoryItem[], history: ActionHistoryItem[]) => {
  return history.length
    ? simulatedActionHistory
        .slice(
          0,
          simulatedActionHistory.findIndex(i => i.actionId === history[0].actionId),
        )
        .concat(history)
    : [];
};

export const selectActionFeed = createSelector(
  [entity, feed, actionHistory, register, mode, simulatedActionHistory],
  parseActionFeed,
);

// Optional optimized version with sliding window
export const selectActionFeedOptimized = (windowSize: number = 10) =>
  createSelector(
    [entity, feed, actionHistory, register, mode, simulatedActionHistory],
    (entity, queue, history, register, mode, simulatedActionHistory) =>
      parseActionFeed(entity, queue, history, register, mode, simulatedActionHistory, windowSize),
  );

export function parseActionFeed(
  entity: IEntityReducer,
  queue: IQueueItem[],
  history: ActionHistory,
  register: IRegisterReducer,
  mode: Mode,
  simulatedActionHistory,
  windowSize?: number, // Optional window size for optimization
): IActionFeed {
  const fullFeed = queue.flatMap(item => {
    if (item.feedback) {
      return [
        {
          feedback: true,
          sessionAttemptId: item.sessionAttemptId,
          courseProgressId: item.courseProgressId,
          actionRegisterId: null,
        },
      ];
    } else if (item.moduleId) {
      return nextModule(
        item.sessionAttemptId,
        entity,
        item.moduleId,
        mode === Mode.ADMIN
          ? simulatedActionHistory
          : mode === Mode.PREVIEW
          ? mergeHistory(simulatedActionHistory, history[item.sessionAttemptId] ?? [])
          : history[item.sessionAttemptId] ?? [],
      );
    } else return [{ courseNodeId: item.courseNodeId, actionRegisterId: null }];
  });

  const index = register.index;

  // If windowSize is provided, use sliding window optimization
  if (windowSize && windowSize > 0) {
    const startIndex = Math.max(0, index - Math.floor(windowSize / 2));
    const endIndex = Math.min(fullFeed.length, startIndex + windowSize);

    const optimizedFeed = fullFeed.slice(startIndex, endIndex);
    const adjustedIndex = Math.min(index - startIndex, optimizedFeed.length - 1);

    return {
      feed: optimizedFeed,
      index: Math.max(0, adjustedIndex),
      next: register.isNext,
    } as IActionFeed;
  }

  // Default behavior: return full feed
  return {
    feed: fullFeed,
    index,
    next: register.isNext,
  } as IActionFeed;
}

function processModOut(entity, modOut: IModuleOut) {
  if (!modOut) return null;

  return parseAction(entity, modOut.targetId);
}

function addActionToFuture(
  sessionAttemptId: number,
  entity: IEntityReducer,
  action: IActionBasic,
  future: IActionFeedItem[],
  history: ActionHistoryItem[],
  first: boolean,
  order?: number[],
) {
  if (!action || action.status === ActionStatus.DELETED) return;
  if (!first && future.some(f => f.actionId === action.id) && !order) return;

  // Limit future actions to prevent excessive memory usage
  //if (future.length >= 10) return;

  if (!first)
    future.push({
      actionId: action.id,
      sceneId: action.sceneId,
      actionType: action.type,
      moduleId: action.moduleId,
      sessionAttemptId: sessionAttemptId,
      actionRegisterId: null,
    });

  const next = processModOut(entity, action.outs[Handles.DEFAULT]);

  if (next && next.status > -1 && next.id > 0)
    addActionToFuture(sessionAttemptId, entity, next, future, history, false);
  else if (order) {
    let currentIndex = order.indexOf(action.id);
    if (currentIndex === -1) {
      const lastHistoryInOrder = [...history].reverse().find(h => order.includes(h.actionId));
      currentIndex = lastHistoryInOrder ? order.indexOf(lastHistoryInOrder.actionId) : -1;
    }
    if (currentIndex !== -1 && currentIndex + 1 < order.length)
      addActionToFuture(
        sessionAttemptId,
        entity,
        parseAction(entity, order[currentIndex + 1]),
        future,
        history,
        false,
        order,
      );
  } else if (action.childId && !future.some(f => f.actionId === action.childId)) {
    const child = parseActionDAO(entity, action.childId);
    if (child && child.status > -1 && child.id > 0)
      return addActionToFuture(sessionAttemptId, entity, child, future, history, false);
  } else {
    // Fallback: try to continue from the grandparent's next child
    const parent = history[history.length - 2];

    if (parent) {
      const parentAction = parseAction(entity, parent.actionId);
      const nextId = parentAction.outs[Handles.DEFAULT]?.targetId;
      if (nextId && nextId !== action.id && !future.some(f => f.actionId === nextId))
        addActionToFuture(sessionAttemptId, entity, parseAction(entity, nextId), future, history, false);
    }
  }
}

function nextModule(
  sessionAttemptId: number,
  entity: IEntityReducer,
  moduleId: number,
  history: ActionHistoryItem[],
): IActionFeedItem[] {
  const module = entity[EntityTypes.MODULE][moduleId] as IModuleAssign;

  if (!module) {
    console.warn('[actionFeed] nextModule — module not in entity store yet', { moduleId, sessionAttemptId });
    return [];
  }

  if ([ModuleType.YOUTUBE, ModuleType.SCORM, ModuleType.WEBSITE].includes(module.type)) {
    return [
      {
        moduleId: module.id,
        sessionAttemptId: module.sessionAttemptId || sessionAttemptId,
        actionRegisterId: null,
      },
    ];
  }

  let future = [];

  if (history.length && ![ModuleType.ASSESSMENT, ModuleType.ASSESSMENT_RANDOM].includes(module.type)) {
    future = history.map(historyItem => {
      return {
        ...historyItem,
        moduleId: module.id,
        sessionAttemptId: sessionAttemptId,
        actionRegisterId: historyItem.actionRegisterId ?? null,
      };
    });

    addActionToFuture(
      sessionAttemptId,
      entity,
      parseAction(entity, future[future.length - 1].actionId),
      future,
      history,
      true,
      module.order,
    );
    if ([ModuleType.ASSESSMENT, ModuleType.ASSESSMENT_RANDOM].includes(module.type))
      future.push({
        moduleId: module.id,
        sessionAttemptId: sessionAttemptId,
        feedback: true,
      });
  } else if (module.order) {
    addActionToFuture(
      sessionAttemptId,
      entity,
      parseAction(entity, module.order[0]),
      future,
      history,
      false,
      module.order,
    );
    if ([ModuleType.ASSESSMENT, ModuleType.ASSESSMENT_RANDOM].includes(module.type))
      future.push({
        moduleId: module.id,
        sessionAttemptId: sessionAttemptId,
        feedback: true,
      });
  } else if (module.actionId)
    addActionToFuture(sessionAttemptId, entity, parseAction(entity, module.actionId), future, history, false);
  else {
    const actions = getEntities(entity, EntityTypes.ACTION, 'moduleId', module.id).map(a => parseAction(entity, a.id));

    const action = getFirstAction(actions);

    if (action) addActionToFuture(sessionAttemptId, entity, parseAction(entity, action.id), future, history, false);
  }

  return future;
}
